import { captureException } from '@sentry/react';

/** Fraction of sessions that validate and report. */
const SESSION_SAMPLE_RATE = 0.1;

/**
 * What `useClient` needs from a response validator: an api-types operation such as
 * `Central.V3.Users.Get.List` satisfies it, from any domain. Only the fields the validator reads are listed,
 * so a breaking change in api-types' `ParseResult` fails to compile at each config.
 */
export type ResponseParser<R> = {
  parse: (
    raw: unknown
  ) =>
    | { success: true; data: R }
    | { success: false; error: { details: { code: string; message: string; path: PropertyKey[] }[] } };
};

export class ResponseValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ResponseValidationError';
  }
}

type ResponseValidation = {
  data: unknown;
  parse: ResponseParser<unknown>['parse'];
  source: 'callApi' | 'callInterval' | 'callNextPage';
  /** Resolved request URL, for debugging context only. */
  url: string;
  /** Config URL template (`@id` placeholders intact), used for grouping and dedupe. */
  urlTemplate: string;
};

type ResponseValidatorOptions = {
  /** Source of randomness for the sampling decision. */
  random?: () => number;
  /** Fraction of sessions (validator instances) that validate and report. */
  sampleRate: number;
};

/** Array indexes become `*`, so the same field failing on many list items reads as one issue. */
const normalizePath = (path: PropertyKey[]): string =>
  path.map((segment) => (typeof segment === 'number' ? '*' : String(segment))).join('.');

/**
 * Create a validator that checks a GET response against its config's `responseSchema` and reports
 * mismatches to Sentry. Each instance decides once whether it is sampled, and reports each URL
 * template at most once: a schema mismatch is a contract bug, not a per-request event, so later
 * polls and page changes add nothing.
 *
 * Report-only: never throws and never replaces the data, since parsing strips unknown keys.
 * Issues carry paths, codes and messages only; the payload itself is never sent.
 */
export const createResponseValidator = ({ random = Math.random, sampleRate }: ResponseValidatorOptions) => {
  const reported = new Set<string>();
  let isSampled: boolean | undefined;

  return ({ data, parse, source, url, urlTemplate }: ResponseValidation): void => {
    isSampled ??= random() < sampleRate;
    if (!isSampled || reported.has(urlTemplate)) return;

    const result = parse(data);
    if (result.success) return;

    reported.add(urlTemplate);

    const issues = new Map<string, { code: string; count: number; message: string; path: string }>();
    for (const { code, message, path } of result.error.details) {
      const normalized = normalizePath(path);
      const key = `${normalized}|${code}|${message}`;
      const existing = issues.get(key);
      if (existing) {
        existing.count++;
      } else {
        issues.set(key, { code, count: 1, message, path: normalized });
      }
    }
    const uniqueIssues = Array.from(issues.values()).slice(0, 20);

    captureException(new ResponseValidationError(`Response does not match schema: GET ${urlTemplate}`), {
      fingerprint: ['response-validation', urlTemplate],
      tags: {
        errorType: 'response-validation',
        feature: 'api-client-response-schema',
      },
      contexts: {
        responseValidation: {
          issueCount: result.error.details.length,
          issues: uniqueIssues,
          summary: uniqueIssues
            .map(({ count, message, path }) => `${path || '(root)'}: ${message}${count > 1 ? ` (x${count})` : ''}`)
            .join('\n'),
          source,
          url,
          urlTemplate,
        },
      },
    });
  };
};

/** The app-wide validator: one per page load, so sampling and dedupe are per session. */
export const validateResponse = createResponseValidator({ sampleRate: SESSION_SAMPLE_RATE });
